import React, { useState, useMemo } from 'react'
import { 
  Card,
  Tag,
  Timeline,
  Button,
  Input,
  Select,
  Pagination,
  message,
  Space
} from 'antd'
import { 
  EditOutlined, 
  DeleteOutlined, 
  SendOutlined,
  RollbackOutlined,
  PushpinOutlined,
  UserOutlined,
  FileTextOutlined,
  LinkOutlined,
  ClockCircleOutlined,
  SearchOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import type { OperationLogItem, OperationLogProps, StatusInfo } from './types'
import { OPERATION_TYPES } from './types'

// 样式常量
const STYLES = {
  container: {
    width: '100%',
  },
  header: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#262626',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '2px solid #ea7303',
  },
  statisticsContainer: {
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: '#fafafa',
    borderRadius: '8px',
    border: '1px solid #f0f0f0'
  },
  statisticsContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
    flexWrap: 'wrap' as const,
    gap: '16px'
  },
  statisticsLeft: {
    flex: 1,
  },
  statisticsTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#262626',
    marginBottom: '12px'
  },
  statisticsGrid: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '12px'
  },
  statisticsItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '6px',
    minWidth: '80px',
    justifyContent: 'center'
  },
  statisticsIcon: {
    fontSize: '16px'
  },
  statisticsCount: {
    fontSize: '16px',
    fontWeight: '600'
  },
  statisticsLabel: {
    fontSize: '11px',
    color: '#666'
  },
  quickActionsContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    minWidth: '200px'
  },
  quickActionsTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#262626',
    marginBottom: '8px'
  },
  quickActionsGrid: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px'
  },
  quickActionButton: {
    textAlign: 'left' as const,
    height: 'auto',
    padding: '4px 8px'
  },
  toolbarContainer: {
    marginBottom: '16px',
    padding: '16px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: '1px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '12px'
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap' as const
  },
  toolbarRight: {
    display: 'flex',
    gap: '8px'
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px'
  },
  operationCard: {
    border: 'none',
    padding: '2px',
    transition: 'all 0.3s ease',
  },
  operationCardBody: {
    padding: '16px',
    backgroundColor: '#fff',
    borderRadius: '6px',
    margin: '0'
  },
  operationCardContent: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start'
  },
  operationIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    border: '2px solid',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  },
  operationIconInner: {
    fontSize: '24px',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  operationContent: {
    flex: 1,
    minWidth: 0
  },
  operationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
    flexWrap: 'wrap' as const,
    gap: '8px'
  },
  operationTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#262626',
  },
  operationTime: {
    fontSize: '12px',
    color: '#666',
    backgroundColor: '#f5f5f5',
    padding: '4px 8px',
    borderRadius: '4px',
    whiteSpace: 'nowrap' as const,
  },
  operationInfoBox: {
    padding: '8px 12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    border: '1px solid #e9ecef',
    marginBottom: '12px',
    position: 'relative' as const,
  },
  operationDescription: {
    padding: '12px',
    backgroundColor: '#fafafa',
    borderRadius: '6px',
    border: '1px solid #f0f0f0',
    marginBottom: '12px',
  },
  statusChangeBox: {
    position: 'absolute' as const,
    top: '0',
    right: '0',
    width: '200px',
    height: '40px',
    padding: '8px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  paginationContainer: {
    textAlign: 'center' as const,
    marginTop: '24px',
    padding: '16px',
    backgroundColor: '#fafafa',
    borderRadius: '8px',
    border: '1px solid #f0f0f0',
  },
  emptyContainer: {
    textAlign: 'center' as const,
    padding: '40px',
    color: '#999',
    backgroundColor: '#fafafa',
    borderRadius: '8px',
    border: '1px solid #f0f0f0'
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
    color: '#ccc'
  },
} as const

const OperationLog: React.FC<OperationLogProps> = ({
  data = [],
  total = 0,
  current = 1,
  pageSize = 10,
  loading = false,
  onPageChange,
  onExport,
  title = '操作记录',
  showStatistics = true,
  showQuickActions = true,
  showSearch = true,
  showExport = true,
  showPagination = true,
  className,
  style,
  operationTypeNames,
  statusMap,
  iconMap,
  themeColor = '#ea7303',
  themeBgColor = '#fff7e6',
}) => {
  // 状态管理
  const [viewMode, setViewMode] = useState<'card' | 'timeline'>('card')
  const [searchText, setSearchText] = useState('')
  const [filterType, setFilterType] = useState<number | 'all'>('all')

  // 默认操作类型名称映射
  const defaultOperationTypeNames: Record<number, string> = {
    [OPERATION_TYPES.CREATE]: '创建',
    [OPERATION_TYPES.UPDATE]: '更新',
    [OPERATION_TYPES.PUBLISH]: '发布',
    [OPERATION_TYPES.WITHDRAW]: '撤回',
    [OPERATION_TYPES.DELETE]: '删除',
    [OPERATION_TYPES.PUSH]: '推送',
  }

  // 默认状态映射
  const defaultStatusMap: Record<number, StatusInfo> = {
    1: { text: '草稿', color: '#d9d9d9', bgColor: '#fafafa' },
    2: { text: '已发布', color: '#52c41a', bgColor: '#f6ffed' },
    3: { text: '已撤回', color: '#fa8c16', bgColor: '#fff7e6' },
  }

  // 默认图标映射
  const defaultIconMap: Record<number, React.ReactNode> = {
    [OPERATION_TYPES.CREATE]: <UserOutlined />,
    [OPERATION_TYPES.UPDATE]: <EditOutlined />,
    [OPERATION_TYPES.PUBLISH]: <SendOutlined />,
    [OPERATION_TYPES.WITHDRAW]: <RollbackOutlined />,
    [OPERATION_TYPES.DELETE]: <DeleteOutlined />,
    [OPERATION_TYPES.PUSH]: <PushpinOutlined />,
  }

  // 合并自定义配置
  const finalOperationTypeNames = { ...defaultOperationTypeNames, ...operationTypeNames }
  const finalStatusMap = { ...defaultStatusMap, ...statusMap }
  const finalIconMap = { ...defaultIconMap, ...iconMap }

  // 过滤数据
  const filteredData = useMemo(() => {
    return data.filter(item => {
      // 按操作类型过滤
      if (filterType !== 'all' && filterType !== undefined && item.operationType !== filterType) {
        return false
      }
      
      // 按搜索文本过滤
      if (searchText) {
        const searchTextLower = searchText.toLowerCase()
        const operationDesc = (item.operationDesc || '').toLowerCase()
        const operatorName = (item.operatorNickname || item.operatorUsername || '').toLowerCase()
        const operationTypeName = (item.operationTypeName || '').toLowerCase()
        
        return operationDesc.includes(searchTextLower) || 
               operatorName.includes(searchTextLower) || 
               operationTypeName.includes(searchTextLower)
      }
      
      return true
    })
  }, [data, filterType, searchText])

  // 统计数据 - 基于原始数据，不受过滤影响
  const statistics = useMemo(() => {
    return data.reduce((acc, item) => {
      const type = item.operationType
      acc[type] = (acc[type] || 0) + 1
      return acc
    }, {} as Record<number, number>)
  }, [data])

  // 获取操作图标
  const getOperationIcon = (operationType: number) => {
    return finalIconMap[operationType] || <ClockCircleOutlined />
  }

  // 获取操作描述
  const getOperationDescription = (item: OperationLogItem) => {
    return item.operationDesc || '执行了操作'
  }

  // 获取状态变更信息
  const getStatusChangeInfo = (item: OperationLogItem) => {
    if (item.beforeStatus === undefined && item.afterStatus === undefined) {
      return null
    }

    const beforeStatus = finalStatusMap[item.beforeStatus as keyof typeof finalStatusMap]
    const afterStatus = finalStatusMap[item.afterStatus as keyof typeof finalStatusMap]

    if (!beforeStatus && !afterStatus) return null

    return (
      <div style={STYLES.statusChangeBox}>
        <span style={{ fontSize: '12px', color: '#666' }}>状态变更：</span>
        {item.beforeStatus !== undefined && beforeStatus && (
          <Tag 
            style={{ 
              backgroundColor: beforeStatus.bgColor,
              borderColor: beforeStatus.color,
              color: beforeStatus.color,
              margin: 0
            }}
          >
            {item.beforeStatusName || beforeStatus.text}
          </Tag>
        )}
        <span style={{ color: '#666', fontSize: '14px' }}>→</span>
        {item.afterStatus !== undefined && afterStatus && (
          <Tag 
            style={{ 
              backgroundColor: afterStatus.bgColor,
              borderColor: afterStatus.color,
              color: afterStatus.color,
              margin: 0
            }}
          >
            {item.afterStatusName || afterStatus.text}
          </Tag>
        )}
      </div>
    )
  }

  // 格式化时间
  const formatDateTime = (date?: string) => {
    return date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '-'
  }

  // 渲染卡片视图
  const renderCardView = () => (
    <div style={STYLES.cardContainer}>
      {filteredData.map((item, index) => {
        const statusChangeInfo = getStatusChangeInfo(item)
        
        return (
          <Card
            key={item.id || index}
            size="small"
            style={{
              ...STYLES.operationCard,
              background: `linear-gradient(135deg, ${themeColor} 0%, #722ed1 100%)`,
              boxShadow: `0 4px 12px ${themeColor}20`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = `0 8px 24px ${themeColor}40`
              e.currentTarget.style.transform = 'translateY(-2px)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}20`
              e.currentTarget.style.transform = 'translateY(0)'
            }}
            bodyStyle={STYLES.operationCardBody}
            hoverable
          >
            <div style={STYLES.operationCardContent}>
              {/* 左侧操作图标 */}
              <div 
                style={{
                  ...STYLES.operationIcon,
                  background: themeBgColor,
                  borderColor: themeColor,
                  color: themeColor,
                  boxShadow: `0 2px 8px ${themeColor}15`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)'
                  e.currentTarget.style.boxShadow = `0 4px 16px ${themeColor}25`
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)'
                  e.currentTarget.style.boxShadow = `0 2px 8px ${themeColor}15`
                }}
              >
                <div style={{
                  ...STYLES.operationIconInner,
                  color: themeColor,
                }}>
                  {getOperationIcon(item.operationType)}
                </div>
              </div>

              {/* 右侧操作详情 */}
              <div style={STYLES.operationContent}>
                {/* 操作标题和时间 */}
                <div style={STYLES.operationHeader}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={STYLES.operationTitle}>
                      {item.operationTypeName || finalOperationTypeNames[item.operationType] || `类型${item.operationType}`}
                    </span>
                    <Tag 
                      color="orange"
                      style={{
                        backgroundColor: themeBgColor,
                        borderColor: themeColor,
                        color: themeColor,
                        margin: 0,
                        fontSize: '11px',
                        padding: '0 6px',
                        height: '20px',
                        lineHeight: '18px'
                      }}
                    >
                      {item.operationType}
                    </Tag>
                  </div>
                  <div style={STYLES.operationTime}>
                    <ClockCircleOutlined style={{ marginRight: '4px' }} />
                    {formatDateTime(item.operationTime)}
                  </div>
                </div>

                {/* 操作人信息 */}
                <div style={STYLES.operationInfoBox}>
                  <UserOutlined style={{ color: themeColor, marginRight: '4px' }} />
                  <span style={{ fontSize: '12px', color: '#666' }}>操作人：</span>
                  <span style={{ fontWeight: '500', color: '#262626' }}>
                    {item.operatorNickname || item.operatorUsername}
                  </span>
                  {item.operatorUsername && item.operatorNickname && (
                    <span style={{ fontSize: '11px', color: '#999' }}>
                      ({item.operatorUsername})
                    </span>
                  )}
                  {statusChangeInfo}
                </div>

                {/* 操作描述 */}
                {getOperationDescription(item) && (
                  <div style={STYLES.operationDescription}>
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      操作描述：
                    </div>
                    <div style={{
                      color: '#262626',
                      lineHeight: '1.5'
                    }}>
                      {getOperationDescription(item)}
                    </div>
                  </div>
                )}

                {/* 其他详细信息 */}
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  fontSize: '11px',
                  color: '#999',
                  padding: '8px 0',
                  borderTop: '1px solid #f0f0f0',
                  marginTop: '8px'
                }}>
                  {item.id && (
                    <span>操作ID: {item.id}</span>
                  )}
                  {item.operationTime && (
                    <span>• 操作时间: {formatDateTime(item.operationTime)}</span>
                  )}
                  {item.operatorId && (
                    <span>• 操作人ID: {item.operatorId}</span>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )
      })}
    </div>
  )

  // 渲染时间轴视图
  const renderTimelineView = () => (
    <Timeline
      items={filteredData.map((item, index) => {
        const statusChangeInfo = getStatusChangeInfo(item)
        
        return {
          color: themeColor,
          children: (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                marginBottom: '8px'
              }}>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>
                  {item.operationTypeName || finalOperationTypeNames[item.operationType] || `类型${item.operationType}`}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {formatDateTime(item.operationTime)}
                </div>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>操作人：</span>
                <span style={{ fontWeight: '500' }}>{item.operatorNickname || item.operatorUsername}</span>
              </div>
              {getOperationDescription(item) && (
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#666' }}>操作描述：</span>
                  <span>{getOperationDescription(item)}</span>
                </div>
              )}
              {statusChangeInfo && (
                <div style={{ marginBottom: '8px' }}>
                  {statusChangeInfo}
                </div>
              )}
            </div>
          ),
        }
      })}
    />
  )

  // 处理导出
  const handleExport = () => {
    if (onExport) {
      onExport(filteredData)
    } else {
      // 默认导出逻辑
      const csvContent = [
        ['操作类型', '操作描述', '操作人', '操作时间', '状态变更'],
        ...filteredData.map(item => [
          item.operationTypeName || finalOperationTypeNames[item.operationType] || '',
          item.operationDesc || '',
          item.operatorNickname || item.operatorUsername || '',
          formatDateTime(item.operationTime),
          item.beforeStatus !== undefined || item.afterStatus !== undefined 
            ? `${item.beforeStatusName || ''} → ${item.afterStatusName || ''}`
            : ''
        ])
      ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n')
      
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `操作记录_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`
      link.click()
      message.success('操作记录导出成功')
    }
  }

  return (
    <div className={className} style={{ ...STYLES.container, ...style }}>
      {/* 标题 */}
      <div style={STYLES.header}>
        <ClockCircleOutlined style={{ marginRight: '8px' }} />
        {title} ({total})
      </div>

      {/* 统计信息和快速操作 */}
      {showStatistics && (
        <div style={STYLES.statisticsContainer}>
          <div style={STYLES.statisticsContent}>
            <div style={STYLES.statisticsLeft}>
              <div style={STYLES.statisticsTitle}>
                📊 操作统计
              </div>
              <div style={STYLES.statisticsGrid}>
                {Object.entries(statistics).map(([type, count]) => {
                  const operationType = Number(type)
                  const operationTypeName = finalOperationTypeNames[operationType] || `类型${operationType}`
                  
                  return (
                    <div
                      key={type}
                      style={{
                        ...STYLES.statisticsItem,
                        backgroundColor: themeBgColor,
                        border: `1px solid ${themeColor}`,
                      }}
                    >
                      <div style={{
                        ...STYLES.statisticsIcon,
                        color: themeColor
                      }}>
                        {getOperationIcon(operationType)}
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}>
                        <span style={{
                          ...STYLES.statisticsCount,
                          color: themeColor
                        }}>
                          {count}
                        </span>
                        <span style={STYLES.statisticsLabel}>
                          {operationTypeName}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
            
            {showQuickActions && (
              <div style={STYLES.quickActionsContainer}>
                <div style={STYLES.quickActionsTitle}>
                  ⚡ 快速操作
                </div>
                <div style={STYLES.quickActionsGrid}>
                  <Button
                    size="small"
                    type="text"
                    icon={<ClockCircleOutlined />}
                    onClick={() => {
                      setFilterType('all')
                      setSearchText('')
                      message.success('已重置所有过滤条件')
                    }}
                    style={STYLES.quickActionButton}
                  >
                    重置过滤条件
                  </Button>
                  <Button
                    size="small"
                    type="text"
                    icon={<FileTextOutlined />}
                    onClick={() => {
                      setViewMode('card')
                      message.success('已切换到卡片视图')
                    }}
                    style={STYLES.quickActionButton}
                  >
                    切换到卡片视图
                  </Button>
                  <Button
                    size="small"
                    type="text"
                    icon={<ClockCircleOutlined />}
                    onClick={() => {
                      setViewMode('timeline')
                      message.success('已切换到时间轴视图')
                    }}
                    style={STYLES.quickActionButton}
                  >
                    切换到时间轴视图
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 搜索和过滤工具栏 */}
      {showSearch && (
        <div style={STYLES.toolbarContainer}>
          <div style={STYLES.toolbarLeft}>
            <Input.Search
              placeholder="搜索操作描述、操作人..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '250px' }}
              allowClear
            />
            <Select
              placeholder="选择操作类型"
              value={filterType}
              onChange={(value) => setFilterType(value || 'all')}
              style={{ width: '150px' }}
              allowClear
            >
              <Select.Option value="all">全部类型</Select.Option>
              {Object.entries(finalOperationTypeNames).map(([type, name]) => (
                <Select.Option key={type} value={Number(type)}>{name}</Select.Option>
              ))}
            </Select>
            {/* 显示当前过滤条件 */}
            {(searchText || filterType !== 'all') && (
              <div style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginLeft: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>当前过滤：</span>
                {filterType !== 'all' && (
                  <Tag color="blue" style={{ fontSize: '11px', padding: '0 6px', height: '20px', lineHeight: '18px' }}>
                    类型: {finalOperationTypeNames[filterType as number] || `类型${filterType}`}
                  </Tag>
                )}
                {searchText && (
                  <Tag color="green" style={{ fontSize: '11px', padding: '0 6px', height: '20px', lineHeight: '18px' }}>
                    搜索: {searchText}
                  </Tag>
                )}
                <span style={{ color: '#999' }}>
                  (显示 {filteredData.length} / {data.length} 条记录)
                </span>
              </div>
            )}
          </div>
          
          <div style={STYLES.toolbarRight}>
            <Button
              type={viewMode === 'card' ? 'primary' : 'default'}
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => setViewMode('card')}
            >
              卡片视图
            </Button>
            <Button
              type={viewMode === 'timeline' ? 'primary' : 'default'}
              size="small"
              icon={<ClockCircleOutlined />}
              onClick={() => setViewMode('timeline')}
            >
              时间轴视图
            </Button>
            {showExport && (
              <Button
                size="small"
                icon={<LinkOutlined />}
                onClick={handleExport}
              >
                导出CSV
              </Button>
            )}
          </div>
        </div>
      )}
      
      {/* 根据视图模式渲染内容 */}
      {filteredData.length === 0 ? (
        <div style={STYLES.emptyContainer}>
          <SearchOutlined style={STYLES.emptyIcon} />
          <div>没有找到匹配的操作记录</div>
          <div style={{ fontSize: '12px', marginTop: '8px', color: '#ccc' }}>
            请尝试调整搜索条件或过滤条件
          </div>
        </div>
      ) : (
        viewMode === 'card' ? renderCardView() : renderTimelineView()
      )}

      {/* 分页器 */}
      {showPagination && total > pageSize && (
        <div style={STYLES.paginationContainer}>
          <Pagination
            current={current}
            pageSize={pageSize}
            total={total}
            onChange={(page) => onPageChange?.(page)}
            showSizeChanger={false}
            showQuickJumper
            showTotal={(total, range) => (
              <span style={{ color: '#666', fontSize: '14px' }}>
                第 <strong>{range[0]}</strong>-<strong>{range[1]}</strong> 条，
                共 <strong style={{ color: themeColor }}>{total}</strong> 条记录
              </span>
            )}
            itemRender={(page, type, originalElement) => {
              if (type === 'page') {
                return (
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'all 0.3s ease'
                  }}>
                    {page}
                  </span>
                )
              }
              return originalElement
            }}
          />
        </div>
      )}
    </div>
  )
}

export default OperationLog
